import './VsPlayer.css'
import React, {useContext, useEffect, useState} from "react";
import {ThemeContext} from "../../providers/themeContext/theme-context.tsx";
import {PageEnum, PageProps} from "../../types/IPage.ts";
import SettingModal from "../home/component/SettingModal.tsx";
import PlayTipModal from "../home/component/PlayTipsModal.tsx";
import DifficultySelectionDialog from "./components/DifficultySelectionDialog/DifficultySelectionDialog.tsx";
import BattleLinkDialog from "./components/BattleLink/BattleLinkDialog.tsx";
import JoinMatchDialog from "./components/JoinMatchDialog/JoinMatchDialog.tsx";
import MyMatchDialog from "./components/MyMatchDialog/MyMatchDialog.tsx";
import {apiService} from "../../common/utils/http/api-service.ts";
import useLoading from "../../providers/loadingContext/useLoading.ts";
import {ToastContext} from "../../components/CustomToast/ToastProvider.tsx";
import {IVSPlayerPageProps} from "../../types/IVSPlayer.ts";
import {GAEvents} from "../../common/utils/GA4/GAEvents.ts";
import {IVSPlayerRoomInfo} from "../../common/utils/http/IResponse.ts";
import {useMusicContext} from "../../providers/musicProvider/music-player-context.tsx";
import CustomProfile from "../../components/CustomProfile/CustomProfile.tsx";

const VsPlayerHomePage: React.FC<PageProps & IVSPlayerPageProps> = ({switchPage, setVsPlayerInfo}) => {
    // 主题全局管理器
    const {imgUrls} = useContext(ThemeContext);
    const {showLoading, hideLoading} = useLoading();
    const {addToast} = useContext(ToastContext);
    // 全局音乐provider
    const musicManager = useMusicContext();
    // 是否显示游戏提示
    const [showPlayTip, setShowPlayTip] = useState<boolean>(false)
    // 是否弹出设置弹窗
    const [showSetting, setShowSetting] = useState<boolean>(false)
    // 是否显示难度选择弹窗
    const [showDifficultySelectionDialog, setShowDifficultySelectionDialog] = useState(false)
    // 是否显示对战链接弹窗
    const [showBattleLinkDialog, setShowBattleLinkDialog] = useState(false)
    // 是否显示加入游戏的弹窗
    const [showJoinMatchDialog, setShowJoinMatchDialog] = useState<boolean>(false)
    // 是否显示我的游戏记录弹窗
    const [showMyWatchDialog, setShowMyWatchDialog] = useState<boolean>(false)
    // 房间号id
    const [roomId, setRoomId] = useState<string>("")
    // 是否显示分享弹窗的开始按钮
    const [hideShareDialogStartBtn, setHideDialogShareStartBtn] = useState(false)
    // 是否显示对方用户弹窗
    const [showOtherPlayerProfile, setShowOtherPlayerProfile] = useState<boolean>(false)
    // 对方用户id
    const [otherUserId, setOtherUserId] = useState<string | null>(null)


    useEffect(() => {
        // GA 埋点
        GAEvents.vsPlayerPageShow()
    }, []);

    // 打开how to play提示弹窗
    const openHowToPlayModal = () => {
        setShowPlayTip(true)
        musicManager.playButtonSound()
    }

    // 打开设置弹窗
    const openSettingModal = () => {
        musicManager.playButtonSound()
        setShowSetting(true)
    }

    // 返回按钮的点击事件
    const handleBackButton = () => {
        // 游戏开始按钮的点击事件
        switchPage(PageEnum.HOME)
        musicManager.playButtonSound()
    }

    // 在设置中点击游戏提示
    const showPlayTipModal = () => {
        // 关闭设置弹窗
        setShowSetting(false)
        // 显示游戏提示
        setShowPlayTip(true)
        musicManager.playButtonSound()
    }

    // 游戏选择弹窗打开
    const openDifficultySelectionDialog = () => {
        // GA埋点
        GAEvents.vsPlayerPageClickCard("Create Match")
        musicManager.playButtonSound()
        setShowDifficultySelectionDialog(true)
    }

    // 显示加入游戏弹窗
    const showJoinMatch = () => {
        // GA埋点
        GAEvents.vsPlayerPageClickCard("Join Match")
        setShowJoinMatchDialog(true)
        musicManager.playButtonSound()
    }

    // 显示我的游戏列表弹窗
    const showMyWatch = () => {
        // GA埋点
        GAEvents.vsPlayerPageClickCard("My Matches")
        musicManager.playButtonSound()
        setShowMyWatchDialog(true)
    }

    // 打开分享弹窗
    const openShareModal = (room: IVSPlayerRoomInfo) => {
        setRoomId(room.id)
        setHideDialogShareStartBtn(true)
        setShowBattleLinkDialog(true)
        musicManager.playButtonSound()
    }

    // 创建房间
    const createRoomByHardLevel = async (hardLevel: number) => {
        try {
            showLoading();
            const response = await apiService.createVsPlayerRoom(hardLevel);
            if (response.code === 200) {
                setRoomId(response.data.id)
                if (setVsPlayerInfo) {
                    setVsPlayerInfo(response.data)
                }
            } else {
                addToast({
                    type: "error",
                    message: response.message,
                })
                setRoomId("")
            }
            // 关闭难度选择弹窗
            setShowDifficultySelectionDialog(false)
            setHideDialogShareStartBtn(false)
            // 显示对战链接弹窗
            setShowBattleLinkDialog(true)
        } catch (e) {
            console.log(e)
            addToast({message: "Create vs player room failed.", type: "error"})
        } finally {
            hideLoading()
        }
    }

    // 关闭分享弹窗
    const closeShareModal = () => {
        setShowBattleLinkDialog(false)
        setRoomId("")
        musicManager.playButtonSound()
    }

    // 打开对方的用户信息页
    const openOpponentUserInfoPage = (userId: string) => {
        setOtherUserId(userId)
        setShowOtherPlayerProfile(true)
    }

    return (
        <div className="home-page">
            <img className="common-embellish-bg-left-top-page" src={imgUrls.leftTopBgUrl} alt="pic"/>
            <img className="common-embellish-bg-right-bottom-page" src={imgUrls.rightBottomBgUrl} alt="pic"/>

            {/*<style>{`body { background-color: #F1F4FF }`}</style>*/}
            <div className="display-flex-column">
                {/*顶部标题栏*/}
                <div className="top-title-container" style={{backgroundImage: `url(${imgUrls.titleBarBgUrl})`}}>
                    <img onClick={handleBackButton} className="go-back-button" src={imgUrls.goBackIconUrl} alt="pic"/>
                    <img onClick={openHowToPlayModal} className="tip-button" src={imgUrls.gameTipIconUrl}
                         alt="pic"/>
                    <img onClick={openSettingModal} className="game-setting-button" src={imgUrls.settingIconUrl}
                         alt="pic"/>
                </div>
            </div>
            {/*顶部图标*/}
            <img className="width-53 height-53 margin-top-63" src={imgUrls.vsPlayTopIcon} alt="pic"/>
            {/*标题文字*/}
            <div className="my-roboto-bold font-size-21 color-cyan margin-top-10">VS Player</div>
            {/*添加比赛*/}
            <div onClick={openDifficultySelectionDialog}
                 className="margin-top-17 common-card-item first-card-bg-color display-flex-row display-flex-align-items-center display-flex-space-between">
                <div className="margin-left-22 display-flex-column margin-right-14">
                    <div className="my-roboto-bold font-size-21 color-white">Create Match</div>
                    <div className="my-roboto-regular font-size-16 color-white margin-top-6">Share the link with your
                        friends
                    </div>
                </div>
                <img className="margin-right-14 height-40 width-40" src={imgUrls.firstArrowRightIcon} alt="pic"/>
            </div>
            <div onClick={showJoinMatch}
                 className="margin-top-10 common-card-item second-card-bg-color display-flex-row display-flex-align-items-center display-flex-space-between">
                <div className="margin-left-22 display-flex-column margin-right-14">
                    <div className="my-roboto-bold font-size-21 color-white">Join Match</div>
                    <div className="my-roboto-regular font-size-16 color-white margin-top-6">Enter the shared link to
                        play with your friends
                    </div>
                </div>
                <img className="margin-right-14 height-40 width-40" src={imgUrls.secondArrowRightIcon} alt="pic"/>
            </div>
            <div onClick={showMyWatch}
                 className="margin-top-10 common-card-item third-card-bg-color display-flex-row display-flex-align-items-center display-flex-space-between">
                <div className="margin-left-22 display-flex-column margin-right-14">
                    <div className="my-roboto-bold font-size-21 color-white">My Matches</div>
                    <div className="my-roboto-regular font-size-16 color-white margin-top-6">View your match history
                    </div>
                </div>
                <img className="margin-right-14 height-40 width-40" src={imgUrls.thirdArrowRightIcon} alt="pic"/>
            </div>
            {/*设置的弹窗*/}
            <SettingModal key="homeSettingModal" isOpen={showSetting} onClose={() => setShowSetting(false)}
                          onShowPlayTip={showPlayTipModal}/>
            {/*游戏提示的弹窗*/}
            <PlayTipModal key="playTipModal" isOpen={showPlayTip} onClose={() => setShowPlayTip(false)}/>
            {/*游戏难度选择弹窗*/}
            <DifficultySelectionDialog isOpen={showDifficultySelectionDialog}
                                       onClose={() => setShowDifficultySelectionDialog(false)}
                                       createRoomByHardLevel={createRoomByHardLevel}/>
            {/*加入对战弹窗*/}
            <JoinMatchDialog isOpen={showJoinMatchDialog} onClose={() => setShowJoinMatchDialog(false)}
                             switchPage={switchPage} setVsPlayerInfo={setVsPlayerInfo!}/>
            {/*my match弹窗*/}
            <MyMatchDialog isOpen={showMyWatchDialog} onClose={() => setShowMyWatchDialog(false)}
                           share={openShareModal}
                           showOtherUserProfile={openOpponentUserInfoPage}
                           switchPage={switchPage} setVsPlayerInfo={setVsPlayerInfo!}/>
            {/*对战链接分享弹窗*/}
            <BattleLinkDialog isOpen={showBattleLinkDialog} onClose={closeShareModal}
                              isShowInWaitingPage={hideShareDialogStartBtn}
                              roomId={roomId} switchPage={switchPage}/>
            <CustomProfile isOpen={showOtherPlayerProfile} onClose={() => setShowOtherPlayerProfile(false)}
                           userId={otherUserId}/>
        </div>
    )
}

export default VsPlayerHomePage;